import dayjs from "dayjs";

export const KIND_LABEL = { group: "Group", vp: "Function (VP)", cluster: "Clusters", outlet: "Outlets" };

const Delta = ({ delta }) => {
  if (delta === null || delta === undefined) return null;
  if (delta > 0) return <div className="dr-up">▲ {delta} vs yest.</div>;
  if (delta < 0) return <div className="dr-down">▼ {Math.abs(delta)} vs yest.</div>;
  return <div className="dr-down" style={{ color: "#6B7280" }}>= vs yest.</div>;
};

const redL5Color = (redNum, tier) => {
  if (redNum > 0) return "#BB162B"; // crimson: red backlog present
  if (tier === "clean") return "#BF9B30"; // gold: celebrating a clean day
  return "#2E7D32"; // green: healthy
};

const CardBand = ({ r }) => (
  <div className={`dr-band dr-b-${r.band}`}>
    <div className="dr-brand">Incheon CX Command Center</div>
    <h2>{r.title}</h2>
    <div className="dr-date">{dayjs(r.date).format("dddd, D MMMM YYYY")} · {r.sub}</div>
    <span className="dr-tier-chip" data-testid="daily-report-tier-chip">{r.tier_emoji} {r.tier_label}</span>
    <div className="dr-verdict">{r.verdict}</div>
  </div>
);

const CleanCelebration = ({ r }) => {
  if (r.tier !== "clean") return null;
  return (
    <div className="dr-celebrate">
      <div className="dr-big">🏆</div>
      <p>
        <b>{r.streak > 1 ? `That's ${r.streak} Clean Days in a row.` : "A perfectly clean day."}</b>
        {r.best_streak?.length > 0 && (
          <>
            <br />
            Best streak this month: {r.best_streak.title}, {r.best_streak.length} day{r.best_streak.length !== 1 ? "s" : ""}.
          </>
        )}
      </p>
    </div>
  );
};

const CardKpis = ({ r }) => {
  const k = r.kpis;
  const redNum = k.red_count;
  const showStreak = r.tier === "clean" && r.streak > 0;
  return (
    <div className="dr-kpis" style={r.tier === "clean" ? { borderTop: "1px solid #E3E5EA" } : undefined}>
      <div className="dr-kpi">
        <div className="dr-n">{k.open_now}</div>
        <div className="dr-l">Open</div>
        <Delta delta={k.delta} />
      </div>
      <div className="dr-kpi"><div className="dr-n">{k.new_today}</div><div className="dr-l">New today</div></div>
      <div className="dr-kpi"><div className="dr-n">{k.closed_today}</div><div className="dr-l">Closed today</div></div>
      <div className="dr-kpi">
        <div className="dr-n" style={{ color: redL5Color(redNum, r.tier) }}>
          {showStreak ? `${r.streak}🔥` : redNum}
        </div>
        <div className="dr-l">{showStreak ? "Streak" : "Red · L5"}</div>
      </div>
    </div>
  );
};

const AGING_BAR_COLORS = ["dr-a-g", "dr-a-a", "dr-a-r", "dr-a-dr"];

const AgingSection = ({ r }) => {
  const openTotal = r.kpis.open_now;
  if (openTotal <= 0) return null;
  const agingTotal = r.aging.reduce((s, a) => s + a.count, 0) || 1;
  return (
    <div className="dr-sec">
      <h3>Aging of {openTotal} open complaint{openTotal !== 1 ? "s" : ""}</h3>
      <div className="dr-agebar">
        {r.aging.map((a, i) => a.count > 0 && (
          <div key={a.bucket} className={AGING_BAR_COLORS[i]} style={{ width: `${(a.count / agingTotal) * 100}%` }} />
        ))}
      </div>
      <div className="dr-agekey">
        {r.aging.map((a) => <span key={a.bucket}>{a.bucket}d · {a.count}</span>)}
      </div>
    </div>
  );
};

const ComplaintMiniTable = ({ title, rows, daysCell }) => {
  if (!rows.length) return null;
  return (
    <div className="dr-sec">
      <h3>{title}</h3>
      <table>
        <thead>
          <tr><th>Complaint</th><th>Outlet</th><th>Category</th><th>Days</th></tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.complaint_number}>
              <td className="dr-cmp">{o.complaint_number}</td>
              <td>{o.outlet}</td>
              <td>{o.category}</td>
              {daysCell(o)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OutletMinis = ({ r }) => {
  if (!r.best_outlet && !r.worst_outlet) return null;
  return (
    <div className="dr-sec">
      <div className="dr-row2">
        {r.best_outlet && (
          <div className="dr-mini">
            <div className="dr-t">Best outlet today</div>
            <div className="dr-v dr-best">🏆 {r.best_outlet.name}</div>
            <div className="dr-note">{r.best_outlet.note}</div>
          </div>
        )}
        {r.worst_outlet && (
          <div className="dr-mini">
            <div className="dr-t">Needs focus</div>
            <div className="dr-v dr-worst">{r.worst_outlet.name}</div>
            <div className="dr-note">{r.worst_outlet.note}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const FunctionSplitTable = ({ split }) => {
  if (!split) return null;
  return (
    <div className="dr-sec">
      <h3>Function split</h3>
      <table>
        <thead>
          <tr><th>Function</th><th>Open</th><th>Red</th><th>Escalation</th></tr>
        </thead>
        <tbody>
          {split.map((fs) => (
            <tr key={fs.function}>
              <td>{fs.function}</td>
              <td>{fs.open}</td>
              <td style={fs.red > 0 ? { color: "#BB162B", fontWeight: 700 } : undefined}>{fs.red}</td>
              <td>
                {fs.l5 > 0
                  ? <span className="dr-lvl dr-lvl-crimson">L5 ×{fs.l5}</span>
                  : <span className="dr-lvl">{fs.max_level > 0 ? `max L${fs.max_level}` : "—"}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StreakFooter = ({ r }) => {
  if (r.tier === "clean" || !(r.best_streak?.length > 0)) return null;
  return (
    <div className="dr-streak">
      <span className="dr-flame">🔥</span>
      <div className="dr-txt">
        <b>Best streak this month: {r.best_streak.title}, {r.best_streak.length} day{r.best_streak.length !== 1 ? "s" : ""}.</b>
        {r.reopened_month > 0 && <> {r.reopened_month} reopened this month — watch for premature closures.</>}
      </div>
    </div>
  );
};

export const DailyReportCard = ({ r, onOpenDashboard }) => (
  <div className="dr-card" data-testid={`daily-report-card-${r.scope_key}`}>
    <CardBand r={r} />
    <CleanCelebration r={r} />
    <CardKpis r={r} />
    <AgingSection r={r} />
    <ComplaintMiniTable
      title={r.kpis.l5_count > 0 ? "At MD level (L5) — oldest first" : "Oldest open in your scope"}
      rows={r.oldest || []}
      daysCell={(o) => <td className="dr-days-red">{o.days} d</td>}
    />
    <ComplaintMiniTable
      title="Watch tomorrow"
      rows={(r.watch || []).filter((w) => w.next_level)}
      daysCell={(w) => <td className="dr-days-amber">{w.days} d → L{w.next_level}</td>}
    />
    <OutletMinis r={r} />
    <FunctionSplitTable split={r.function_split} />
    <StreakFooter r={r} />
    {r.adoption?.line && (
      <div className="dr-adoption" data-testid="daily-report-adoption-line">{r.adoption.line}</div>
    )}
    {(r.google_line?.line || r.insight_line || r.standings_line) && (
      <div className="dr-google" data-testid="daily-report-google-block">
        {r.standings_line && <div data-testid="daily-report-standings-line"><b>{r.standings_line}</b></div>}
        {r.google_line?.line && <div data-testid="daily-report-google-line">{r.google_line.line}</div>}
        {r.insight_line && <div data-testid="daily-report-insight-line">{r.insight_line}</div>}
      </div>
    )}
    {r.negative_review_line && (
      <div className="dr-negative" data-testid="daily-report-negative-line">{r.negative_review_line}</div>
    )}
    {r.oem_line && (
      <div className="dr-oem" data-testid="daily-report-oem-line">{r.oem_line}</div>
    )}
    <div className="dr-stamps" data-testid="daily-report-stamps">
      <span>Generated on: {r.generated_on_fmt || "—"}</span>
      <span>Based on data uploaded: {r.based_on_fmt || "—"}</span>
    </div>
    <div className="dr-foot">
      <span className="dr-logo">INCHEON KIA</span>
      <button className="dr-cta" onClick={onOpenDashboard} data-testid="daily-report-open-dashboard">Open Dashboard →</button>
    </div>
  </div>
);
